import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PublicIcon from "@mui/icons-material/Public";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import {
  REFERENCE_RANGE_GENDERS,
  DROPDOWN_OPTION_PRESETS,
  MAX_DROPDOWN_OPTIONS,
} from "config/reportTemplateOptions";
import { CSV_LIST_SEPARATOR } from "utils/reportTemplateExport";

const titleSx = { fontWeight: 700, fontSize: 17, color: "#111827" };
const subtitleSx = { fontSize: 12.5, color: "#6B7280", mt: 0.25 };

const DialogShell = ({
  open,
  title,
  subtitle,
  onCancel,
  onSave,
  readOnly,
  error,
  children,
  maxWidth = "sm",
}) => (
  <Dialog
    open={open}
    onClose={onCancel}
    maxWidth={maxWidth}
    fullWidth
    PaperProps={{ sx: { borderRadius: 3 } }}
  >
    <DialogTitle sx={{ pb: 1 }}>
      <Typography sx={titleSx}>{title}</Typography>
      {subtitle && <Typography sx={subtitleSx}>{subtitle}</Typography>}
    </DialogTitle>
    <DialogContent dividers sx={{ bgcolor: "#F9FAFB" }}>
      {children}
      {error && (
        <Typography sx={{ mt: 1.5, fontSize: 13, color: "#DC2626" }}>
          {error}
        </Typography>
      )}
    </DialogContent>
    <DialogActions sx={{ px: 3, py: 2, bgcolor: "#fff" }}>
      <Button
        onClick={onCancel}
        sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600 }}
      >
        {readOnly ? "Close" : "Cancel"}
      </Button>
      {!readOnly && (
        <Button
          variant="contained"
          onClick={onSave}
          disabled={Boolean(error)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            borderRadius: 2,
          }}
        >
          Save
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

const ScopeChip = ({ global }) => (
  <Chip
    size="small"
    icon={
      global ?
        <PublicIcon sx={{ fontSize: 13 }} />
      : <LocalHospitalIcon sx={{ fontSize: 13 }} />
    }
    label={global ? "Global" : "Facility"}
    title={
      global ?
        "Global — Managed by DRCFO Admin"
      : "Facility — Managed by your Hospital/Clinic"
    }
    sx={{
      height: 20,
      fontSize: 10.5,
      fontWeight: 600,
      bgcolor: global ? "#EFF6FF" : "#ECFDF5",
      color: global ? "#1D4ED8" : "#047857",
      "& .MuiChip-icon": { color: global ? "#1D4ED8" : "#047857" },
    }}
  />
);

const isGlobalMaster = (row) => !row?.clinicId;

const emptyRangeDraft = () =>
  REFERENCE_RANGE_GENDERS.reduce((acc, g) => {
    acc[g.value] = {
      normalRangeText: "",
      normalRangeMin: "",
      normalRangeMax: "",
    };
    return acc;
  }, {});

const toRangeDraft = (referenceRanges) => {
  const draft = emptyRangeDraft();
  (referenceRanges || []).forEach((range) => {
    if (!draft[range?.gender]) return;
    draft[range.gender] = {
      normalRangeText: range.normalRangeText ?? "",
      normalRangeMin: range.normalRangeMin ?? "",
      normalRangeMax: range.normalRangeMax ?? "",
    };
  });
  return draft;
};

const fromRangeDraft = (draft) =>
  REFERENCE_RANGE_GENDERS.map(({ value }) => {
    const row = draft[value] || {};
    const filled = [row.normalRangeText, row.normalRangeMin, row.normalRangeMax]
      .map((v) => (v ?? "").toString().trim())
      .some(Boolean);
    if (!filled) return null;
    return {
      gender: value,
      normalRangeText: (row.normalRangeText ?? "").toString(),
      normalRangeMin:
        row.normalRangeMin === "" || row.normalRangeMin === null ?
          null
        : Number(row.normalRangeMin),
      normalRangeMax:
        row.normalRangeMax === "" || row.normalRangeMax === null ?
          null
        : Number(row.normalRangeMax),
    };
  }).filter(Boolean);

export const ReferenceRangesDialog = ({
  open,
  parameter,
  readOnly,
  onCancel,
  onSave,
}) => {
  const [draft, setDraft] = useState(emptyRangeDraft);

  useEffect(() => {
    if (open) setDraft(toRangeDraft(parameter?.referenceRanges));
  }, [open, parameter]);

  const error = useMemo(() => {
    const bad = REFERENCE_RANGE_GENDERS.find(({ value }) => {
      const row = draft[value] || {};
      return (
        row.normalRangeMin !== "" &&
        row.normalRangeMax !== "" &&
        Number(row.normalRangeMin) > Number(row.normalRangeMax)
      );
    });
    return bad ?
        `${bad.label}: Minimum must be less than or equal to Maximum.`
      : "";
  }, [draft]);

  const setField = (gender, field, value) =>
    setDraft((prev) => ({
      ...prev,
      [gender]: { ...prev[gender], [field]: value },
    }));

  return (
    <DialogShell
      open={open}
      title="Configure Reference Ranges"
      subtitle={`Optional gender-specific ranges for "${parameter?.name || "this parameter"}". The parameter's own Reference Range stays the default for everyone.`}
      onCancel={onCancel}
      onSave={() => onSave(fromRangeDraft(draft))}
      readOnly={readOnly}
      error={error}
    >
      {REFERENCE_RANGE_GENDERS.map(({ value, label }) => (
        <Box
          key={value}
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "flex-start",
            mb: 2,
            p: 1.5,
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 2,
          }}
        >
          <Typography
            sx={{ width: 64, pt: 1.2, fontWeight: 700, fontSize: 13.5 }}
          >
            {label}
          </Typography>
          <TextField
            size="small"
            label="Reference Range"
            placeholder="13–17 g/dL"
            value={draft[value]?.normalRangeText ?? ""}
            disabled={readOnly}
            onChange={(e) => setField(value, "normalRangeText", e.target.value)}
            sx={{ flex: 1.4 }}
          />
          <TextField
            size="small"
            type="number"
            label="Min"
            value={draft[value]?.normalRangeMin ?? ""}
            disabled={readOnly}
            onChange={(e) => setField(value, "normalRangeMin", e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="Max"
            value={draft[value]?.normalRangeMax ?? ""}
            disabled={readOnly}
            onChange={(e) => setField(value, "normalRangeMax", e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>
      ))}
      <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
        Leave a row empty to fall back to the default range.
      </Typography>
    </DialogShell>
  );
};

const methodId = (entry) =>
  entry?.method?._id ? String(entry.method._id) : String(entry?.method ?? "");

export const MethodsDialog = ({
  open,
  parameter,
  methods = [],
  readOnly,
  onCancel,
  onSave,
}) => {
  const [selected, setSelected] = useState([]);
  const [defaultId, setDefaultId] = useState("");

  useEffect(() => {
    if (!open) return;
    const entries = parameter?.methods || [];
    setSelected(entries.map(methodId).filter(Boolean));
    setDefaultId(entries.filter((e) => e?.isDefault).map(methodId)[0] || "");
  }, [open, parameter]);

  const toggle = (id) =>
    setSelected((prev) => {
      const next =
        prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (!next.includes(defaultId)) setDefaultId("");
      return next;
    });

  const handleSave = () =>
    onSave(
      selected.map((id) => ({
        method: id,
        isDefault: id === defaultId,
      })),
    );

  return (
    <DialogShell
      open={open}
      title="Configure Methods"
      subtitle={`Methods available for "${parameter?.name || "this parameter"}". Only active methods from the Method Master are listed.`}
      onCancel={onCancel}
      onSave={handleSave}
      readOnly={readOnly}
      maxWidth="sm"
    >
      {!methods.length ?
        <Typography sx={{ fontSize: 13, color: "#6B7280", py: 2 }}>
          No active methods are available yet. Add them in the Method Master
          first.
        </Typography>
      : <>
          <Box
            sx={{
              display: "flex",
              px: 1.5,
              pb: 0.75,
              fontSize: 11.5,
              fontWeight: 700,
              color: "#6B7280",
              textTransform: "uppercase",
            }}
          >
            <Box sx={{ flex: 1 }}>Available Methods</Box>
            <Box sx={{ width: 90, textAlign: "center" }}>Default</Box>
          </Box>
          {methods.map((method) => {
            const id = String(method._id);
            const isSelected = selected.includes(id);
            return (
              <Box
                key={id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  bgcolor: "#fff",
                  border: "1px solid #E5E7EB",
                  borderRadius: 2,
                  px: 1.5,
                  mb: 1,
                }}
              >
                <FormControlLabel
                  sx={{ flex: 1, mr: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      disabled={readOnly}
                      onChange={() => toggle(id)}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>
                        {method.name}
                      </Typography>
                      {method.reportDisplayName &&
                        method.reportDisplayName !== method.name && (
                          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                            ({method.reportDisplayName})
                          </Typography>
                        )}
                      <ScopeChip global={isGlobalMaster(method)} />
                    </Box>
                  }
                />
                <Box sx={{ width: 90, textAlign: "center" }}>
                  <Tooltip
                    title={
                      isSelected ? "Default method" : (
                        "Select the method before making it default"
                      )
                    }
                  >
                    <span>
                      <Radio
                        size="small"
                        checked={defaultId === id}
                        disabled={readOnly || !isSelected}
                        onChange={() => setDefaultId(id)}
                      />
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
          <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 1 }}>
            At most one method can be the default. Methods are managed in the
            Method Master — this screen only references them.
          </Typography>
        </>
      }
    </DialogShell>
  );
};

export const CalculationDialog = ({
  open,
  parameter,
  calculations = [],
  readOnly,
  onCancel,
  onSave,
}) => {
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (!open) return;
    const current = parameter?.calculation;
    setSelected(current?._id ? String(current._id) : String(current ?? ""));
  }, [open, parameter]);

  const chosen = calculations.find((c) => String(c._id) === selected);

  return (
    <DialogShell
      open={open}
      title="Configure Calculation"
      subtitle={`Link "${parameter?.name || "this parameter"}" to a Calculation Master entry. The formula itself always stays in the master.`}
      onCancel={onCancel}
      onSave={() => onSave(selected || null)}
      readOnly={readOnly}
    >
      <FormControl fullWidth size="small" disabled={readOnly}>
        <InputLabel>Calculation</InputLabel>
        <Select
          value={selected}
          label="Calculation"
          onChange={(e) => setSelected(e.target.value)}
        >
          <MenuItem value="">
            <em style={{ color: "#9CA3AF" }}>Not calculated</em>
          </MenuItem>
          {calculations.map((calculation) => (
            <MenuItem key={calculation._id} value={String(calculation._id)}>
              {calculation.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {chosen && (
        <Box
          sx={{
            mt: 2,
            p: 1.75,
            bgcolor: "#fff",
            border: "1px solid #E5E7EB",
            borderRadius: 2,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
            Output parameter
          </Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 1 }}>
            {chosen.outputParameterName || "—"}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
            Decimal places
          </Typography>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
            {chosen.decimalPlaces ?? "—"}
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <ScopeChip global={isGlobalMaster(chosen)} />
          </Box>
        </Box>
      )}

      <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 2 }}>
        A calculated parameter keeps the Number input type. Results are computed
        at result entry in a later release.
      </Typography>
    </DialogShell>
  );
};

export const OptionsDialog = ({
  open,
  parameter,
  readOnly,
  onCancel,
  onSave,
}) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (open) setOptions([...(parameter?.options || [])]);
  }, [open, parameter]);

  const error = useMemo(() => {
    const filled = options.map((o) => (o ?? "").trim()).filter(Boolean);
    if (filled.some((o) => o.includes(CSV_LIST_SEPARATOR))) {
      return `An option cannot contain the "${CSV_LIST_SEPARATOR}" character.`;
    }
    const lowered = filled.map((o) => o.toLowerCase());
    if (new Set(lowered).size !== lowered.length) {
      return "Options must be unique.";
    }
    return "";
  }, [options]);

  const setOption = (index, value) =>
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)));
  const addOption = () => setOptions((prev) => [...prev, ""]);
  const removeOption = (index) =>
    setOptions((prev) => prev.filter((_, i) => i !== index));

  const applyPreset = (preset) => setOptions([...preset]);

  return (
    <DialogShell
      open={open}
      title="Configure Options"
      subtitle={`Values the technician can pick for "${parameter?.name || "this parameter"}".`}
      onCancel={onCancel}
      onSave={() =>
        onSave(options.map((o) => (o ?? "").trim()).filter(Boolean))
      }
      readOnly={readOnly}
      error={error}
    >
      {!readOnly && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
          {DROPDOWN_OPTION_PRESETS.map((preset) => (
            <Chip
              key={preset.join("/")}
              size="small"
              variant="outlined"
              label={preset.join(" / ")}
              onClick={() => applyPreset(preset)}
              sx={{ fontSize: 11.5 }}
            />
          ))}
        </Box>
      )}

      {options.length === 0 && (
        <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 2 }}>
          No options yet. Add them below, or pick a suggested set above.
        </Typography>
      )}

      {options.map((option, index) => (
        <Box
          key={index}
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
        >
          <TextField
            fullWidth
            size="small"
            placeholder="Reactive"
            value={option}
            disabled={readOnly}
            onChange={(e) => setOption(index, e.target.value)}
          />
          {!readOnly && (
            <Tooltip title="Remove option">
              <IconButton
                size="small"
                color="error"
                onClick={() => removeOption(index)}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {!readOnly && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={addOption}
          disabled={options.length >= MAX_DROPDOWN_OPTIONS}
          sx={{ textTransform: "none", fontWeight: 600, mt: 0.5 }}
        >
          Add Option
        </Button>
      )}
    </DialogShell>
  );
};
